import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, PERMISSION, can, inboxApi, useAuth, type Agent, type Tag } from '@wa/shared';
import { Avatar } from '@/components/Avatar';

/**
 * The kebab beside the selection count.
 *
 * Every action is one request over the whole set. Forty conversations assigned one
 * at a time is forty round trips, forty transactions and forty chances to
 * half-finish; these either happen or they do not.
 *
 * <b>There is no Delete chat, and its absence is the one deliberate difference
 * from the design being matched.</b> Messages are the record of what this company
 * told a customer — quoted in disputes, and what a DPDP request is answered from.
 * Retention removes them, on a timer, as a policy. A menu item that erases forty
 * conversations because an inbox looked untidy would be the most destructive
 * control on this screen, and it would sit two pixels below "Mark as read".
 * <b>Close</b> stands in its place, and a per-conversation <i>Clear chat</i>
 * already exists for an agent who wants a clean pane without destroying anything.
 */
interface Props {
  ids: number[];
  onDone: () => void;
}

type Sheet = null | 'tags' | 'assign' | 'lists';

export function BulkActions({ ids, onDone }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['counts'] });
  };

  const fail = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'That did not work.');

  /** Every action reports how many rows it actually changed, not how many were
      selected. Re-running one over an overlapping selection legitimately changes
      nothing, and "0 changed" is the honest answer to that. */
  const ran = (verb: string) => (result: { changed: number }) => {
    setError(null);
    setDone(`${verb} ${result.changed} of ${ids.length}.`);
    setSheet(null);
    refresh();
    if (result.changed > 0) onDone();
  };

  const markRead = useMutation({
    mutationFn: () => inboxApi.bulkRead(ids),
    onSuccess: ran('Marked read'), onError: fail,
  });
  const close = useMutation({
    mutationFn: () => inboxApi.bulkStatus(ids, 2),
    onSuccess: ran('Closed'), onError: fail,
  });
  const assign = useMutation({
    mutationFn: (agentId: number | null) => inboxApi.bulkAssign(ids, agentId),
    onSuccess: ran('Assigned'), onError: fail,
  });
  const tag = useMutation({
    mutationFn: (tagId: number) => inboxApi.bulkTag(ids, tagId),
    onSuccess: ran('Tagged'), onError: fail,
  });

  const busy = markRead.isPending || close.isPending || assign.isPending || tag.isPending;

  const items: { key: string; label: string; hint?: string; icon: React.ReactNode; run: () => void; hidden?: boolean; divider?: boolean }[] = [
    { key: 'read', label: 'Mark as read', hint: 'Clears the badge; the customer sees blue ticks', icon: <IconTicks />, run: () => markRead.mutate() },
    { key: 'tags', label: 'Add a tag', hint: 'The same tag on every selected chat', icon: <IconTag />, run: () => setSheet('tags'),
      hidden: !can(user, PERMISSION.inboxSend) },
    { key: 'assign', label: 'Assign to…', hint: 'Hand them to one person', icon: <IconPerson />, run: () => setSheet('assign'),
      hidden: !can(user, PERMISSION.inboxAssign) },
    { key: 'unassign', label: 'Un-assign', hint: 'Back to the unassigned queue', icon: <IconPersonOff />, run: () => assign.mutate(null),
      hidden: !can(user, PERMISSION.inboxAssign) },
    { key: 'lists', label: 'Add to a broadcast list', hint: 'The contacts behind these chats', icon: <IconList />, run: () => setSheet('lists'),
      hidden: !can(user, PERMISSION.segmentsWrite), divider: true },
    { key: 'close', label: 'Close chats', hint: 'Resolved; a reply from the customer reopens', icon: <IconClose />, run: () => close.mutate(),
      hidden: !can(user, PERMISSION.inboxSend), divider: true },
  ].filter((i) => !i.hidden);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => { setError(null); setDone(null); setOpen((v) => !v); }}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${ids.length} selected`}
        className="tap grid size-7 place-items-center rounded-(--radius-field) text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink) disabled:opacity-50"
      >
        <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
          <circle cx="10" cy="4.2" r="1.5" fill="currentColor" />
          <circle cx="10" cy="10" r="1.5" fill="currentColor" />
          <circle cx="10" cy="15.8" r="1.5" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)"
        >
          <p className="px-3 pt-1.5 pb-1 text-micro font-semibold tracking-wide text-(--color-faint) uppercase">
            {ids.length} selected
          </p>
          {items.map((item) => (
            <div key={item.key}>
              {item.divider && <div className="my-1 border-t border-(--color-line-dim)" />}
              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); item.run(); }}
                className="tap flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-(--color-sunken)"
              >
                <span className="mt-0.5 grid size-5 shrink-0 place-items-center text-(--color-muted)">{item.icon}</span>
                <span className="min-w-0">
                  <span className="block text-small font-medium text-(--color-ink)">{item.label}</span>
                  {item.hint && <span className="block text-micro leading-snug text-(--color-muted)">{item.hint}</span>}
                </span>
              </button>
            </div>
          ))}
        </div>
      )}

      {sheet === 'tags' && (
        <Picker
          title={`Tag ${ids.length} ${ids.length === 1 ? 'chat' : 'chats'}`}
          subtitle="Pick one tag; it goes on every selected chat. Tags already there are kept."
          queryKey={['tags']}
          load={inboxApi.tags}
          search={(t: Tag) => t.name}
          render={(t: Tag) => (
            <>
              <span className="size-3 shrink-0 rounded-full" style={{ background: t.colour ?? 'var(--color-faint)' }} aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-small font-medium">{t.name}</span>
              <span className="shrink-0 text-micro text-(--color-muted)">{t.conversationCount} {t.conversationCount === 1 ? 'chat' : 'chats'}</span>
            </>
          )}
          onPick={(t) => tag.mutate(t.id)}
          busy={busy}
          error={error}
          onClose={() => { setError(null); setSheet(null); }}
        />
      )}

      {sheet === 'assign' && (
        <Picker
          title={`Assign ${ids.length} ${ids.length === 1 ? 'chat' : 'chats'}`}
          subtitle="Everyone selected goes to this one person. Their own open count is shown."
          queryKey={['agents']}
          load={inboxApi.agents}
          search={(a: Agent) => `${a.displayName} ${a.email ?? ''}`}
          render={(a: Agent) => (
            <>
              <Avatar name={a.displayName} waId={String(a.id)} size={32} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-small font-medium">
                  {a.displayName}{a.id === user?.id && <span className="ml-1 text-micro font-normal text-(--color-faint)">(you)</span>}
                </span>
                <span className="block truncate text-micro text-(--color-muted)">{a.role ?? a.email ?? ''}</span>
              </span>
            </>
          )}
          onPick={(a) => assign.mutate(a.id)}
          busy={busy}
          error={error}
          onClose={() => { setError(null); setSheet(null); }}
        />
      )}

      {sheet === 'lists' && (
        <BroadcastPicker ids={ids} onDone={ran('Added')} onError={fail}
                         error={error} onClose={() => { setError(null); setSheet(null); }} />
      )}

      {/* Below the control rather than inside a dialog. It reports on something
          already finished, and a dialog for that is a click nobody wanted. */}
      {(done || error) && !sheet && (
        <p
          role="status"
          className={[
            'absolute right-0 top-full z-30 mt-1 w-56 rounded-(--radius-field) border px-2.5 py-1.5 text-micro shadow-sm',
            error
              ? 'border-(--color-bad) bg-(--color-surface) text-(--color-bad)'
              : 'border-(--color-line) bg-(--color-surface) text-(--color-muted)',
          ].join(' ')}
        >
          {error ?? done}
        </p>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- pickers -- */

function Picker<T extends { id: number }>({
  title, subtitle, queryKey, load, render, search, onPick, busy, error, onClose,
}: {
  title: string;
  subtitle?: string;
  queryKey: string[];
  load: () => Promise<T[]>;
  render: (item: T) => React.ReactNode;
  search: (item: T) => string;
  onPick: (item: T) => void;
  busy: boolean;
  error: string | null;
  onClose: () => void;
}) {
  const list = useQuery({ queryKey, queryFn: load, staleTime: 300_000 });
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<number | null>(null);
  const all = list.data ?? [];
  const q = query.trim().toLowerCase();
  const shown = q ? all.filter((item) => search(item).toLowerCase().includes(q)) : all;

  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose}>
      {all.length > 6 && (
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          aria-label="Search"
          className="tap mb-2 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) px-3 py-1.5 text-small outline-none focus:border-(--color-brand) focus-visible:outline-none"
        />
      )}
      {list.isPending && <p className="py-6 text-center text-small text-(--color-muted)">Loading…</p>}
      {!list.isPending && all.length === 0 && (
        <p className="py-6 text-center text-small text-(--color-muted)">There are none to choose from yet.</p>
      )}
      {!list.isPending && all.length > 0 && shown.length === 0 && (
        <p className="py-6 text-center text-small text-(--color-muted)">Nothing matches "{query}".</p>
      )}
      <ul className="-mx-1 max-h-72 overflow-y-auto">
        {shown.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => { setPicked(item.id); onPick(item); }}
              className="tap flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-(--color-sunken) disabled:opacity-60"
            >
              {render(item)}
              {busy && picked === item.id && (
                <span className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-(--color-line) border-t-(--color-brand)" aria-label="Working" />
              )}
            </button>
          </li>
        ))}
      </ul>
      {error && <p role="alert" className="mt-2 rounded border border-(--color-bad) px-2.5 py-1.5 text-small text-(--color-bad)">{error}</p>}
    </Modal>
  );
}

/**
 * "Add to broadcast list" is a contact-level act, so it takes the contacts behind
 * the selected conversations rather than the conversations themselves — and only
 * static lists are offered, because a rule-based list is defined by its rules and
 * adding forty people to one by hand would be a lie about what it contains.
 */
function BroadcastPicker({ ids, onDone, onError, error, onClose }: {
  ids: number[];
  onDone: (result: { changed: number }) => void;
  onError: (e: unknown) => void;
  error: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const segments = useQuery({ queryKey: ['segments'], queryFn: inboxApi.segments, staleTime: 300_000 });

  // The contact ids are already on the rows the page is holding, so this reads
  // them out of the cache rather than asking for forty conversations again.
  const contactIds = (queryClient.getQueriesData<{ items: { id: number; contactId: number }[] }>({ queryKey: ['conversations'] })
    .flatMap(([, data]) => data?.items ?? [])
    .filter((c) => ids.includes(c.id))
    .map((c) => c.contactId));

  const unique = [...new Set(contactIds)];

  const add = useMutation({
    mutationFn: (segmentId: number) => inboxApi.addSegmentMembers(segmentId, unique),
    onSuccess: () => onDone({ changed: unique.length }),
    onError,
  });

  const statics = (segments.data ?? []).filter((s) => s.kind === 0);

  return (
    <Modal title={`Add ${unique.length} ${unique.length === 1 ? 'contact' : 'contacts'} to a list`} subtitle="Only fixed lists; rule-based lists choose their own members." onClose={onClose}>
      {segments.isPending && <p className="text-small text-(--color-muted)">Loading…</p>}

      {!segments.isPending && statics.length === 0 && (
        <p className="text-small text-(--color-muted)">
          There are no fixed lists yet. Rule-based lists decide their own members, so
          nobody can be added to one by hand — make a fixed list under Customers.
        </p>
      )}

      <ul className="-mx-1 max-h-72 overflow-y-auto">
        {statics.map((segment) => (
          <li key={segment.id}>
            <button
              type="button"
              disabled={add.isPending || unique.length === 0}
              onClick={() => add.mutate(segment.id)}
              className="tap flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-(--color-sunken) disabled:opacity-60"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-(--color-sunken) text-(--color-muted)"><IconList /></span>
              <span className="min-w-0 flex-1 truncate text-small font-medium">{segment.name}</span>
            </button>
          </li>
        ))}
      </ul>

      {error && <p role="alert" className="mt-2 text-small text-(--color-bad)">{error}</p>}
    </Modal>
  );
}

function Modal({ title, subtitle, children, onClose }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/35 p-4" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[76vh] w-full max-w-sm flex-col rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) shadow-(--shadow-pop)"
      >
        <header className="flex items-start gap-3 border-b border-(--color-line) px-4 pt-3.5 pb-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lead font-semibold tracking-tight">{title}</h2>
            {subtitle && <p className="mt-0.5 text-micro leading-snug text-(--color-muted)">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="tap -mr-1.5 -mt-1 grid size-8 shrink-0 place-items-center rounded-md text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
        <footer className="flex justify-end border-t border-(--color-line) px-4 py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small font-medium text-(--color-ink) hover:bg-(--color-sunken)"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- icons -- */

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
function IconTicks() { return <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M2.5 10.5 6 14l6.5-7M9.5 14l7-7.5" {...stroke} /></svg>; }
function IconTag() { return <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M3 3h6.5l7.5 7.5-6.5 6.5L3 9.5z" {...stroke} /><circle cx="6.5" cy="6.5" r="1" fill="currentColor" /></svg>; }
function IconPerson() { return <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><circle cx="10" cy="6.5" r="3" {...stroke} /><path d="M4 17a6 6 0 0 1 12 0" {...stroke} /></svg>; }
function IconPersonOff() { return <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><circle cx="9" cy="6.5" r="3" {...stroke} /><path d="M3 17a6 6 0 0 1 9.5-4.9M13 13l4 4M17 13l-4 4" {...stroke} /></svg>; }
function IconList() { return <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M7 5h10M7 10h10M7 15h10" {...stroke} /><circle cx="3.5" cy="5" r="1" fill="currentColor" /><circle cx="3.5" cy="10" r="1" fill="currentColor" /><circle cx="3.5" cy="15" r="1" fill="currentColor" /></svg>; }
function IconClose() { return <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><circle cx="10" cy="10" r="7" {...stroke} /><path d="m7 10.5 2 2 4-4.5" {...stroke} /></svg>; }
